// src/components/notifications/GroupedNotificationItem.tsx
// Renders a grouped notification with count, source app, and expand capability

import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import type {
  GroupedNotification,
  CentralNotification,
  AppMetadata,
} from '../../api/centralNotificationsApi';

// ============================================================
// Icons
// ============================================================

const ChevronDownIcon = ({ className = '' }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const ChevronUpIcon = ({ className = '' }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
  </svg>
);

const CheckIcon = ({ className = '' }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const StackIcon = ({ className = '' }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
    />
  </svg>
);

// ============================================================
// Source App Badge Component
// ============================================================

interface SourceAppBadgeProps {
  appId: string | null;
  appMetadata?: AppMetadata;
}

export const SourceAppBadge: React.FC<SourceAppBadgeProps> = ({ appId, appMetadata }) => {
  // Use enriched metadata if available, otherwise fall back to app ID
  const displayName = appMetadata?.appDisplayName || getDefaultAppName(appId);
  const iconUrl = appMetadata?.appIconUrl;
  const category = appMetadata?.appCategory;

  return (
    <div className="flex items-center gap-1.5 text-xs text-gray-500">
      {iconUrl ? (
        <img
          src={iconUrl}
          alt={displayName}
          className="w-4 h-4 rounded"
          onError={(e) => {
            // Fallback to emoji if image fails
            e.currentTarget.style.display = 'none';
          }}
        />
      ) : (
        <span className="text-sm">{getDefaultAppIcon(appId)}</span>
      )}
      <span className="font-medium">{displayName}</span>
      {category && (
        <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] text-gray-400">
          {category}
        </span>
      )}
    </div>
  );
};

// Default app name/icon mappings (fallback when enriched data unavailable)
function getDefaultAppName(appId: string | null): string {
  if (!appId) return 'System';
  switch (appId) {
    case 'crm':
      return 'CRM';
    case 'levelup-central':
      return 'Central';
    case 'levelup-docs':
      return 'Docs';
    case 'levelup-payments':
      return 'Payments';
    default:
      return appId;
  }
}

function getDefaultAppIcon(appId: string | null): string {
  if (!appId) return '🔔';
  switch (appId) {
    case 'crm':
      return '📊';
    case 'levelup-central':
      return '🏢';
    case 'levelup-docs':
      return '📄';
    case 'levelup-payments':
      return '💳';
    default:
      return '📱';
  }
}

// ============================================================
// Grouped Notification Item Component
// ============================================================

interface GroupedNotificationItemProps {
  group: GroupedNotification;
  onMarkGroupRead: () => void;
  onNotificationClick?: (notification: CentralNotification) => void;
  expandedNotifications?: CentralNotification[];
  isLoadingExpanded?: boolean;
  onExpand?: () => void;
}

export const GroupedNotificationItem: React.FC<GroupedNotificationItemProps> = ({
  group,
  onMarkGroupRead,
  onNotificationClick,
  expandedNotifications,
  isLoadingExpanded,
  onExpand,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const timeAgo = formatDistanceToNow(new Date(group.latestAt), { addSuffix: true });

  const handleToggleExpand = () => {
    if (!isExpanded && onExpand) {
      onExpand();
    }
    setIsExpanded(!isExpanded);
  };

  const handleMarkRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMarkGroupRead();
  };

  return (
    <div
      className={`
        border-b border-gray-100 last:border-b-0
        ${group.isRead ? 'bg-white' : 'bg-blue-50'}
      `}
    >
      {/* Group Header */}
      <div
        className="flex items-start gap-3 p-3 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={handleToggleExpand}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleToggleExpand()}
      >
        {/* Stack icon indicating group */}
        <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center bg-gray-100 rounded-full">
          <StackIcon className="w-5 h-5 text-gray-500" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Source app badge */}
          <SourceAppBadge appId={group.appId} appMetadata={group.appMetadata} />

          {/* Group title */}
          <p className={`text-sm mt-1 ${group.isRead ? 'text-gray-700' : 'text-gray-900 font-medium'}`}>
            {group.groupTitle}
          </p>

          {/* Preview of latest notification */}
          {group.latestNotification.body && (
            <p className="text-xs text-gray-500 truncate mt-0.5">
              {group.latestNotification.body}
            </p>
          )}

          {/* Meta info */}
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-xs text-gray-400">{timeAgo}</span>
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
              {group.count} {group.count === 1 ? 'notification' : 'notifications'}
              {group.unreadCount > 0 && (
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
              )}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {!group.isRead && (
            <button
              onClick={handleMarkRead}
              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
              title="Mark group as read"
            >
              <CheckIcon className="w-4 h-4" />
            </button>
          )}
          <button
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? (
              <ChevronUpIcon className="w-4 h-4" />
            ) : (
              <ChevronDownIcon className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-gray-100 bg-gray-50">
          {isLoadingExpanded ? (
            <div className="p-4 text-center">
              <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-xs text-gray-500 mt-1">Loading...</p>
            </div>
          ) : expandedNotifications && expandedNotifications.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {expandedNotifications.map((notification) => (
                <ExpandedNotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={() => onNotificationClick?.(notification)}
                />
              ))}
            </div>
          ) : (
            <div className="p-4 text-center text-xs text-gray-500">
              No notifications in this group
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================
// Expanded Notification Item (within a group)
// ============================================================

interface ExpandedNotificationItemProps {
  notification: CentralNotification;
  onClick?: () => void;
}

const ExpandedNotificationItem: React.FC<ExpandedNotificationItemProps> = ({
  notification,
  onClick,
}) => {
  const timeAgo = formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true });

  return (
    <div
      className={`
        flex items-start gap-2 px-4 py-2 pl-14 cursor-pointer hover:bg-white transition-colors
        ${notification.isRead ? '' : 'bg-blue-50/50'}
      `}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
    >
      {/* Unread indicator */}
      {!notification.isRead && (
        <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5" />
      )}

      <div className="flex-1 min-w-0">
        <p className={`text-sm ${notification.isRead ? 'text-gray-600' : 'text-gray-900'}`}>
          {notification.title}
        </p>
        {notification.body && (
          <p className="text-xs text-gray-500 truncate">{notification.body}</p>
        )}
        <span className="text-xs text-gray-400">{timeAgo}</span>
      </div>
    </div>
  );
};

export default GroupedNotificationItem;
